import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { Text, View } from "../base";
import { useTheme } from "../../contexts/ThemeContext";
import { formatCount } from "../../utils/adminFormat";
import type { SeriesPoint } from "../../types/admin";

export const ADMIN_CHART_COLORS = {
  signups: "#8B5CF6",
  sessions: "#0891B2",
  leads: "#D97706",
  organizations: "#059669",
};

// Series dates are UTC calendar days ("YYYY-MM-DD"); render them in UTC so the label
// doesn't shift a day in western timezones.
const formatDay = (date: string) => new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

const ChartTooltip = ({ active, payload, valueLabel }: any) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload as SeriesPoint;
  return (
    <View className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 shadow-md">
      <Text className="block text-xs text-gray-500 dark:text-gray-400">{formatDay(point.date)}</Text>
      <Text className="block text-xs font-medium text-gray-900 dark:text-white">
        {formatCount(point.count)} {valueLabel}
      </Text>
    </View>
  );
};

type ActivityBarChartProps = {
  data: SeriesPoint[];
  color: string;
  height?: number;
  valueLabel: string;
};

export const ActivityBarChart = ({ data, color, height = 180, valueLabel }: ActivityBarChartProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const gridColor = isDark ? "#1F2937" : "#E5E7EB";
  const tickColor = "#9CA3AF";

  if (data.length === 0) {
    return (
      <View
        className="flex items-center justify-center"
        style={{ height }}
      >
        <Text className="text-sm text-gray-400 dark:text-gray-500">No data in this period</Text>
      </View>
    );
  }

  return (
    <ResponsiveContainer
      width="100%"
      height={height}
    >
      <BarChart
        data={data}
        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
      >
        <CartesianGrid
          vertical={false}
          stroke={gridColor}
          strokeOpacity={0.35}
        />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tick={{ fill: tickColor, fontSize: 10 }}
          tickFormatter={formatDay}
          interval="preserveStartEnd"
          minTickGap={28}
        />
        <YAxis
          allowDecimals={false}
          tickLine={false}
          axisLine={false}
          width={32}
          tick={{ fill: tickColor, fontSize: 10 }}
        />
        <Tooltip
          content={<ChartTooltip valueLabel={valueLabel} />}
          cursor={{ fill: "rgba(148,163,184,0.1)" }}
        />
        <Bar
          dataKey="count"
          fill={color}
          radius={[4, 4, 0, 0]}
          maxBarSize={28}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};
